import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { SmoothScroll } from "@/components/SmoothScroll";
import Home from "@/pages/Home";
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
import Login from "@/pages/Login";
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
import NotFoundPage from "@/pages/NotFoundPage";
const StudioHome = lazy(() => import("@/pages/studio/StudioHome"));
const StudioDossiers = lazy(() => import("@/pages/studio/StudioDossiers"));
const StudioDossier = lazy(() => import("@/pages/studio/StudioDossier"));
const StudioQuote = lazy(() => import("@/pages/studio/StudioQuote"));
const StudioClients = lazy(() => import("@/pages/studio/StudioClients"));
const StudioLibrary = lazy(() => import("@/pages/studio/StudioLibrary"));
const StudioProfile = lazy(() => import("@/pages/studio/StudioProfile"));
const StudioDocuments = lazy(() => import("@/pages/studio/StudioDocuments"));
const StudioFinances = lazy(() => import("@/pages/studio/StudioFinances"));
const PeppolLanding = lazy(() => import("@/pages/studio/PeppolLanding"));
const CountryLanding = lazy(() => import("@/pages/CountryLanding"));
const DemoPage = lazy(() => import("@/pages/DemoPage"));
const StudioRegister = lazy(() => import("@/pages/studio/StudioRegister"));
const PublicQuote = lazy(() => import("@/pages/studio/PublicQuote"));
const PublicReport = lazy(() => import("@/pages/studio/PublicReport"));
const StudioSubscription = lazy(() => import("@/pages/studio/StudioSubscription"));
const StudioTeam = lazy(() => import("@/pages/studio/StudioTeam"));
const StudioAccountant = lazy(() => import("@/pages/studio/StudioAccountant"));
const StudioAccountantHub = lazy(() => import("@/pages/studio/StudioAccountantHub"));
const StudioShopping = lazy(() => import("@/pages/studio/StudioShopping"));
const StudioIntervention = lazy(() => import("@/pages/studio/StudioIntervention"));
const StudioInterventions = lazy(() => import("@/pages/studio/StudioInterventions"));
const StudioRevenue = lazy(() => import("@/pages/studio/StudioRevenue"));
const StudioInvoices = lazy(() => import("@/pages/studio/StudioInvoices"));
const StudioPayments = lazy(() => import("@/pages/studio/StudioPayments"));
const StudioPurchases = lazy(() => import("@/pages/studio/StudioPurchases"));
const StudioWebRequests = lazy(() => import("@/pages/studio/StudioWebRequests"));
const StudioMessages = lazy(() => import("@/pages/studio/StudioMessages"));
const StudioContracts = lazy(() => import("@/pages/studio/StudioContracts"));
const StudioContract = lazy(() => import("@/pages/studio/StudioContract"));
const StudioAssistant = lazy(() => import("@/pages/studio/StudioAssistant"));
const PublicContract = lazy(() => import("@/pages/studio/PublicContract"));
const PublicFollow = lazy(() => import("@/pages/studio/PublicFollow"));
const PublicReview = lazy(() => import("@/pages/studio/PublicReview"));
const PublicRequest = lazy(() => import("@/pages/studio/PublicRequest"));
const StudioQuotes = lazy(() => import("@/pages/studio/StudioQuotes"));
const StudioOnboarding = lazy(() => import("@/pages/studio/StudioOnboarding"));
const StudioInvoice = lazy(() => import("@/pages/studio/StudioInvoice"));
const StudioPeppol = lazy(() => import("@/pages/studio/StudioPeppol"));
const StudioSupplierInvoices = lazy(() => import("@/pages/studio/StudioSupplierInvoices"));
const Maquette = lazy(() => import("@/pages/Maquette"));
const StudioPlanning = lazy(() => import("@/pages/studio/StudioPlanning"));
const PublicInvoice = lazy(() => import("@/pages/studio/PublicInvoice"));
const PublicPay = lazy(() => import("@/pages/studio/PublicPay"));
const StudioInvitation = lazy(() => import("@/pages/studio/StudioInvitation"));
const StudioSites = lazy(() => import("@/pages/studio/StudioSites"));
const StudioSite = lazy(() => import("@/pages/studio/StudioSite"));
const StudioEquipment = lazy(() => import("@/pages/studio/StudioEquipment"));
const PublicPassport = lazy(() => import("@/pages/studio/PublicPassport"));
const TarifsPage = lazy(() => import("@/pages/TarifsPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const BlogPostPage = lazy(() => import("@/pages/BlogPostPage"));
import { ChatWidget } from "@/components/ChatWidget";
import { CookieBanner } from "@/components/CookieBanner";
import { MaintenanceGate } from "@/components/MaintenanceGate";
import { LanguageProvider } from "@/i18n";
import { MarketProvider } from "@/market";
import { RouteSeo } from "@/seo/Seo";
import { SEO_LANDINGS } from "@/seo/SeoRoutes";
import { useEffect, lazy, Suspense } from "react";
import { useLocation } from "react-router-dom";

/* Le comptable invité ne voit que son espace : on redirige avant que la page cible ne se monte. */
const AccountantGuard = () => {
  const { pathname } = useLocation();
  const accountant = localStorage.getItem("devisto_member_role") === "comptable";
  if (accountant && pathname.startsWith("/studio") && !pathname.startsWith("/studio/comptable") && !pathname.startsWith("/studio/invitation")) {
    return <Navigate to="/studio/comptable" replace />;
  }
  return <Outlet />;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
};

function App() {
  return (
    <LanguageProvider>
    <MarketProvider>
    <div className="App">
      <BrowserRouter>
        <SmoothScroll />
        <ScrollToTop />
        <MaintenanceGate>
        <RouteSeo />
        <Suspense fallback={<div className="min-h-screen bg-[#F6F8FD]" aria-busy="true" />}>
        <Routes>
          <Route element={<AccountantGuard />}>
          <Route path="/" element={<Home />} />
          <Route path="/maquette" element={<Maquette />} />
          <Route path="/connexion" element={<Login />} />
          <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
          <Route path="/reinitialiser/:token" element={<ResetPassword />} />
          <Route path="/tarifs" element={<TarifsPage />} />
          <Route path="/mentions-legales" element={<LegalPage />} />
          <Route path="/confidentialite" element={<PrivacyPage />} />
          <Route path="/cgv" element={<TermsPage />} />
          <Route path="/passeport/:code" element={<PublicPassport />} />
          <Route path="/a-propos" element={<AboutPage />} />
          <Route path="/offre-lancement" element={<Navigate to="/" replace />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          {SEO_LANDINGS.map((r) => <Route key={r.path} path={r.path} element={r.element} />)}
          {/* Anciennes pages marketplace : retirées du site public */}
          {[
            "/publier-une-demande", "/publier-une-mission", "/inscription-entreprise", "/espace/entreprise",
            "/devenir-partenaire-pro", "/espace/pro", "/espace/client", "/espace/property",
            "/inscription-gestionnaire", "/suivi/:token",
            "/trouver/:slug", "/deblocage/:id", "/mission/:id", "/carte", "/assistant-devis",
          ].map((path) => (
            <Route key={path} path={path} element={<Navigate to="/" replace />} />
          ))}
          <Route path="/inscription" element={<Navigate to="/studio/inscription" replace />} />
          <Route path="/studio" element={<StudioHome />} />
          <Route path="/studio/dossiers" element={<StudioDossiers />} />
          <Route path="/studio/dossiers/:id" element={<StudioDossier />} />
          <Route path="/studio/devis/:id" element={<StudioQuote />} />
          <Route path="/studio/clients" element={<StudioClients />} />
          <Route path="/studio/bibliotheque" element={<StudioLibrary />} />
          <Route path="/studio/profil" element={<StudioProfile />} />
          <Route path="/studio/documents" element={<StudioDocuments />} />
          <Route path="/studio/finances" element={<StudioFinances />} />
          <Route path="/facture-peppol-2026" element={<PeppolLanding />} />
          <Route path="/belgique" element={<CountryLanding slug="belgique" />} />
          <Route path="/france" element={<CountryLanding slug="france" />} />
          <Route path="/espagne" element={<CountryLanding slug="espagne" />} />
          <Route path="/luxembourg" element={<CountryLanding slug="luxembourg" />} />
          <Route path="/video-demo" element={<DemoPage />} />
          <Route path="/studio/inscription" element={<StudioRegister />} />
          <Route path="/studio/abonnement" element={<StudioSubscription />} />
          <Route path="/studio/abonnement/succes" element={<StudioSubscription />} />
          <Route path="/studio/equipe" element={<StudioTeam />} />
          <Route path="/studio/comptable" element={<StudioAccountant />} />
          <Route path="/studio/mon-comptable" element={<StudioAccountantHub />} />
          <Route path="/studio/materiel" element={<StudioShopping />} />
          <Route path="/studio/interventions" element={<StudioInterventions />} />
          <Route path="/studio/chiffre-affaires" element={<StudioRevenue />} />
          <Route path="/studio/bienvenue" element={<StudioOnboarding />} />
          <Route path="/studio/devis" element={<StudioQuotes />} />
          <Route path="/studio/factures" element={<StudioInvoices />} />
          <Route path="/studio/avoirs" element={<StudioInvoices creditNotes />} />
          <Route path="/studio/reglements" element={<StudioPayments />} />
          <Route path="/studio/achats" element={<StudioPurchases />} />
          <Route path="/studio/demandes" element={<StudioWebRequests />} />
          <Route path="/studio/messages" element={<StudioMessages />} />
          <Route path="/studio/contrats" element={<StudioContracts />} />
          <Route path="/studio/assistant" element={<StudioAssistant />} />
          <Route path="/studio/contrats/:id" element={<StudioContract />} />
          <Route path="/contrat/:token" element={<PublicContract />} />
          <Route path="/suivi-client/:token" element={<PublicFollow />} />
          <Route path="/avis/:token" element={<PublicReview />} />
          <Route path="/demande/:slug" element={<PublicRequest />} />
          <Route path="/studio/factures/:id" element={<StudioInvoice />} />
          <Route path="/studio/peppol" element={<StudioPeppol />} />
          <Route path="/studio/factures-fournisseurs" element={<StudioSupplierInvoices />} />
          <Route path="/studio/planning" element={<StudioPlanning />} />
          <Route path="/studio/batiments" element={<StudioSites />} />
          <Route path="/studio/batiments/:id" element={<StudioSite />} />
          <Route path="/studio/equipements/:id" element={<StudioEquipment />} />
          <Route path="/pt/:code" element={<PublicPassport />} />
          <Route path="/facture/:token" element={<PublicInvoice />} />
          <Route path="/payer/:token" element={<PublicPay />} />
          <Route path="/studio/interventions/:id" element={<StudioIntervention />} />
          <Route path="/studio/invitation/:token" element={<StudioInvitation />} />
          <Route path="/devis/:token" element={<PublicQuote />} />
          <Route path="/rapport/:token" element={<PublicReport />} />
          <Route path="*" element={<NotFoundPage />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminDashboard />} />
          </Route>
        </Routes>
        </Suspense>
        <ChatWidget />
        <CookieBanner />
        </MaintenanceGate>
        <Toaster position="bottom-center" richColors offset={90} mobileOffset={92} />
      </BrowserRouter>
    </div>
    </MarketProvider>
    </LanguageProvider>
  );
}

export default App;
